import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Paper, 
  Button, 
  IconButton, 
  TextField, 
  Box, 
  Chip, 
  Typography, 
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import CreateListModal from './CreateListModal';
import EditListModal from './EditListModal';
import ViewListModal from './ViewListModal';
import DeleteListConfirmModal from './DeleteListConfirmModal';
import EmailListService from '../../../services/EmailListService';
import dayjs from 'dayjs';

const ListsTab = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [allLists, setAllLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Cargar listas del backend (incluye sistema y personalizadas)
  const loadLists = useCallback(async (page, pageSize, search) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ListsTab] Loading lists - Request:', { page, pageSize, search: search || 'none' });
      
      const response = await EmailListService.getLists(
        page,
        pageSize,
        search || ''
      );
      
      // EmailListService ahora retorna { data: [...], total, page, pageSize }
      const lists = response.data || [];
      const listsArray = Array.isArray(lists) ? lists : [];
      
      console.log('[ListsTab] Loading lists - Success:', {
        totalLists: listsArray.length,
        total: response.total || listsArray.length,
        systemLists: listsArray.filter(l => l.isSystem).length,
        customLists: listsArray.filter(l => !l.isSystem).length
      });
      
      setAllLists(listsArray);
    } catch (err) {
      console.error('[ListsTab] Loading lists - Error:', err);
      console.error('[ListsTab] Loading lists - Error Response:', err.response?.data);
      setError(t('error_loading_lists') || 'Error al cargar las listas');
      setAllLists([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Separar listas del sistema y personalizadas
  const systemLists = React.useMemo(() => {
    return allLists.filter(l => l.isSystem === true);
  }, [allLists]);

  const customLists = React.useMemo(() => {
    return allLists.filter(l => !l.isSystem);
  }, [allLists]);

  // Cargar listas al montar y cuando cambia la paginación
  useEffect(() => {
    loadLists(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [paginationModel.page, paginationModel.pageSize, searchTerm, loadLists]);

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (newSearchTerm) => {
    console.log('[ListsTab] Search term changed:', newSearchTerm || 'empty');
    setSearchTerm(newSearchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      loadLists(1, paginationModel.pageSize, newSearchTerm);
    }, 500);
  };

  // Función para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    console.log('[ListsTab] Pagination changed:', {
      page: newModel.page + 1,
      pageSize: newModel.pageSize,
      searchTerm: searchTerm || 'none'
    });
    setPaginationModel(newModel);
    loadLists(newModel.page + 1, newModel.pageSize, searchTerm);
  };

  // Mostrar todas las listas (sistema y personalizadas)
  const lists = allLists;

  const columns = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 3,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: params.row.isSystem ? 'bold' : 'normal',
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 4,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'created',
      headerName: t('created'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value ? formatDate(dayjs(params.value)) : '-'
    },
    {
      field: 'lastUsed',
      headerName: t('last_used'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value ? formatDate(dayjs(params.value)) : '-'
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1.5,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, height: '100%' }}>
          {!params.row.isSystem && (
            <IconButton 
              size="small" 
              title={t('edit')}
              onClick={() => {
                console.log('[ListsTab] Edit list clicked:', { id: params.row.id, name: params.row.name });
                setSelectedList(params.row);
                setIsEditModalOpen(true);
              }}
              sx={{
                width: 40,
                height: 40,
                minWidth: 40,
                minHeight: 40,
                maxWidth: 40,
                maxHeight: 40,
                padding: 0,
                borderRadius: '50%',
                '&:hover': {
                  borderRadius: '50%'
                }
              }}
            >
              {getIcon('edit')}
            </IconButton>
          )}
          <IconButton 
            size="small" 
            title={t('view')}
            onClick={() => {
              console.log('[ListsTab] View list clicked:', { id: params.row.id, name: params.row.name });
              setSelectedList(params.row);
              setIsViewModalOpen(true);
            }}
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              minHeight: 40,
              maxWidth: 40,
              maxHeight: 40,
              padding: 0,
              borderRadius: '50%',
              '&:hover': {
                borderRadius: '50%'
              }
            }}
          >
            {getIcon('eye')}
          </IconButton>
          {!params.row.isSystem && (
            <IconButton 
              size="small" 
              title={t('delete')} 
              color="error"
              onClick={() => {
                console.log('[ListsTab] Delete list clicked:', { id: params.row.id, name: params.row.name });
                setListToDelete(params.row);
                setIsDeleteDialogOpen(true);
              }}
              sx={{
                width: 40,
                height: 40,
                minWidth: 40,
                minHeight: 40,
                maxWidth: 40,
                maxHeight: 40,
                padding: 0,
                borderRadius: '50%',
                '&:hover': {
                  borderRadius: '50%'
                }
              }}
            >
              {getIcon('trash')}
            </IconButton>
          )}
        </Box>
      )
    }
  ];

  const handleCreateList = () => {
    console.log('[ListsTab] Create list button clicked');
    setIsCreateModalOpen(true);
  };

  const handleSaveNewList = async (listData) => {
    try {
      setLoading(true);
      console.log('[ListsTab] Saving new list - Request:', {
        name: listData.name,
        isClone: !!listData.cloneFromId,
        cloneFromId: listData.cloneFromId || 'none'
      });
      
      // Si hay cloneFromId, usar el método cloneList, sino crear nueva lista
      if (listData.cloneFromId) {
        const { cloneFromId, ...newListData } = listData;
        console.log('[ListsTab] Cloning list from ID:', cloneFromId);
        await EmailListService.cloneList(cloneFromId, newListData);
        console.log('[ListsTab] List cloned successfully');
      } else {
        const { cloneFromId, ...newListData } = listData;
        console.log('[ListsTab] Creating new list from scratch');
        await EmailListService.createList(newListData);
        console.log('[ListsTab] List created successfully');
      }
      setIsCreateModalOpen(false);
      await loadLists(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    } catch (err) {
      console.error('[ListsTab] Error saving new list:', err);
      console.error('[ListsTab] Error response:', err.response?.data);
      setError(t('error_creating_list') || 'Error al crear la lista');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveList = async (listData) => {
    if (!selectedList || !selectedList.id) {
      console.warn('[ListsTab] Cannot update list: no selected list');
      return;
    }
    
    try {
      setLoading(true);
      console.log('[ListsTab] Updating list - Request:', {
        listId: selectedList.id,
        currentName: selectedList.name,
        newName: listData.name
      });
      
      await EmailListService.updateList(selectedList.id, listData);
      
      console.log('[ListsTab] List updated successfully:', {
        listId: selectedList.id,
        newName: listData.name
      });
      
      setIsEditModalOpen(false);
      setSelectedList(null);
      await loadLists(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    } catch (err) {
      console.error('[ListsTab] Error updating list:', err);
      console.error('[ListsTab] Error response:', err.response?.data);
      setError(t('error_updating_list') || 'Error al actualizar la lista');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteList = async () => {
    if (!listToDelete || !listToDelete.id) {
      console.warn('[ListsTab] Cannot delete list: no list selected for deletion');
      return;
    }
    
    try {
      setLoading(true);
      console.log('[ListsTab] Deleting list - Request:', {
        listId: listToDelete.id,
        listName: listToDelete.name,
        isSystem: listToDelete.isSystem
      });
      
      await EmailListService.deleteList(listToDelete.id);
      
      console.log('[ListsTab] List deleted successfully:', {
        listId: listToDelete.id,
        listName: listToDelete.name
      });
      
      setIsDeleteDialogOpen(false);
      setListToDelete(null);
      await loadLists(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    } catch (err) {
      console.error('[ListsTab] Error deleting list:', err);
      console.error('[ListsTab] Error response:', err.response?.data);
      setError(t('error_deleting_list') || 'Error al eliminar la lista');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ padding: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ width: 300 }}
          placeholder={t('search_lists') || 'Buscar listas...'}
        />
        <Button 
          variant="outlined" 
          startIcon={getIcon('add')} 
          onClick={handleCreateList}
          disabled={loading}
        >
          {t('create_list')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && allLists.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <DataGrid
              rows={lists}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              pageSizeOptions={[10, 25, 50]}
              getRowId={(row) => row.id}
              autoHeight
              disableRowSelectionOnClick
              loading={loading}
            />
          </Box>
        </Box>
      )}

      <CreateListModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleSaveNewList}
        allLists={allLists}
      />

      <EditListModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedList(null);
        }}
        onSave={handleSaveList}
        list={selectedList}
      />

      <ViewListModal
        open={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedList(null);
        }}
        list={selectedList}
      />

      <DeleteListConfirmModal
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setListToDelete(null);
        }}
        onConfirm={handleDeleteList}
        list={listToDelete}
        loading={loading}
      />
    </Paper>
  );
};

export default ListsTab;

